import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'

import {
  type CompensationReponse,
  type CompensationResult,
} from 'modules/attendance/components/compensation/payroll/types'

export interface CustomResponseModalProps {
  response: CompensationReponse
  onCloseModal: () => void
}

const CustomResponseModal = ({ response, onCloseModal }: CustomResponseModalProps) => {
  const allEmployeeIds = new Set([
    ...response.successes.map((success) => success.employee_id),
    ...response.errors.map((error) => error.employee_id),
  ]).size

  const uniqueSuccessEmployeeIds = new Set(response.successes.map((success) => success.employee_id))
    .size

  const title = i18n.t(
    'time_tracking.modals.time_tracking_collection.bulk_compensation_response.title'
  )

  return (
    <Modal
      title={title}
      illustration="cash"
      footer={
        <Button onClick={onCloseModal}>
          {i18n.t('time_tracking.modals.time_tracking_collection.bulk_compensation_response.ok')}
        </Button>
      }
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s20" width="full">
        <Text.Normal>
          {i18n.t(
            'time_tracking.modals.time_tracking_collection.bulk_compensation_response.summary',
            {
              created: uniqueSuccessEmployeeIds,
              total: allEmployeeIds,
            }
          )}
        </Text.Normal>

        {response.successes.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal weight="medium">
              {i18n.t(
                'time_tracking.modals.time_tracking_collection.bulk_compensation_response.create.created_for'
              )}
            </Text.Normal>
            <SuccessList successes={response.successes} />
          </Box>
        )}

        {response.errors.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal weight="medium">
              {i18n.t(
                'time_tracking.modals.time_tracking_collection.bulk_compensation_response.create.not_created_for'
              )}
            </Text.Normal>
            <CustomErrorList errors={response.errors} />
          </Box>
        )}
      </Box>
    </Modal>
  )
}

const CustomErrorList = ({ errors }: { errors: CompensationResult[] }) => {
  return (
    <Box
      maxHeight="s200"
      overflowY="scroll"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      width="full"
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gapDivider>
        {errors.map((error) => (
          <CustomErrorItem key={error.employee_id} error={error} />
        ))}
      </Box>
    </Box>
  )
}

const SuccessList = ({ successes }: { successes: CompensationResult[] }) => {
  const uniqueByEmployee = Array.from(new Map(successes.map((s) => [s.employee_id, s])).values())

  return (
    <Box
      maxHeight="s200"
      overflowY="scroll"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      width="full"
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gapDivider>
        {uniqueByEmployee.map((success) => (
          <SuccessItem key={success.employee_id} success={success} />
        ))}
      </Box>
    </Box>
  )
}

const SuccessItem = ({ success }: { success: CompensationResult }) => {
  const employeeName = success.employee_name || ''

  return (
    <Box flexDirection="row" alignItems="center" gap="s16" padding="s16">
      <UserAvatar
        size="s40"
        user={{
          avatar: success.employee_avatar_url || undefined,
          fullName: employeeName,
        }}
      />
      <Text.Normal weight="medium">{employeeName}</Text.Normal>
    </Box>
  )
}

const CustomErrorItem = ({ error }: { error: CompensationResult }) => {
  const employeeName = error.employee_name || ''

  return (
    <Box
      data-testid="employee-error"
      flexDirection="row"
      alignItems="center"
      gap="s16"
      padding="s16"
    >
      <UserAvatar
        size="s40"
        user={{
          avatar: error.employee_avatar_url || undefined,
          fullName: employeeName,
        }}
      />
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s4">
        <Text.Normal weight="medium">{employeeName}</Text.Normal>
        <Text.Normal>{getErrorReason(error.error ?? '')}</Text.Normal>
      </Box>
    </Box>
  )
}

const getErrorReason = (error: string) => {
  switch (error) {
    case 'closed_policy_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.closed_policy_period'
      )
    case 'no_mapping':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_mapping'
      )
    case 'no_overtime':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_overtime'
      )
    case 'no_payroll_pool':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_payroll_pool'
      )
    case 'no_policy_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_policy_period'
      )
    case 'not_assigned_to_any_policy':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.not_assigned_to_any_policy'
      )
    case 'source_id_error':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.source_id_error'
      )
    case 'not_same_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.not_same_period'
      )
    case 'unauthorized_to_create_supplement':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.unauthorized_to_create_supplement'
      )
    case 'unauthorized_to_delete_supplement':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.unauthorized_to_delete_supplement'
      )
    case 'already_compensated':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.already_compensated'
      )
    default:
      return error
  }
}

export default observer(CustomResponseModal)
